import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiconfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ui } from "../config/theme";
import {
    UserIcon,
    ClipboardDocumentListIcon,
    StarIcon,
    PencilIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

const AssessmentPage = () => {
    const { applicationId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [applicationData, setApplicationData] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Assessment form state
    const [showAssessmentForm, setShowAssessmentForm] = useState(false);
    const [assessmentForm, setAssessmentForm] = useState({
        assessment_type: 'Test',
        score: '',
        max_score: 100,
        notes: '',
        status: 'Pending'
    });
    
    // Review form state
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        review_type: 'Overall',
        rating: 5,
        feedback: '',
        strengths: '',
        areas_for_improvement: '',
        recommendation: 'Neutral'
    });

    useEffect(() => {
        if (applicationId) {
            fetchApplicationData();
        }
    }, [applicationId]);

    const fetchApplicationData = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/assessments/application-summary/${applicationId}`);
            const data = await response.json();
            
            if (response.ok) {
                setApplicationData(data.application);
                setAssessments(data.assessments);
                setReviews(data.reviews);
            }
        } catch (error) {
            console.error('Error fetching application data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Determine if the current recruiter has already added a review for this application
    const hasMyReview = Array.isArray(reviews) && reviews.some(r => r.recruiter_id === user?.id);

    const handleCreateAssessment = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/api/assessments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    application_id: applicationId,
                    test_id: null, // Add explicit test_id field
                    recruiter_id: user.id,
                    candidate_id: applicationData.applicant_id,
                    ...assessmentForm
                }),
            });

            if (response.ok) {
                setShowAssessmentForm(false);
                setAssessmentForm({
                    assessment_type: 'Test',
                    score: '',
                    max_score: 100,
                    notes: '',
                    status: 'Pending'
                });
                fetchApplicationData();
            }
        } catch (error) {
            console.error('Error creating assessment:', error);
        }
    };

    const handleCreateReview = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/api/assessments/reviews`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    application_id: applicationId,
                    assessment_id: null, // Add explicit assessment_id field
                    recruiter_id: user.id,
                    candidate_id: applicationData.applicant_id,
                    is_final_review: false, // Add explicit is_final_review field
                    ...reviewForm
                }),
            });

            if (response.ok) {
                setShowReviewForm(false);
                setReviewForm({
                    review_type: 'Overall',
                    rating: 5,
                    feedback: '',
                    strengths: '',
                    areas_for_improvement: '',
                    recommendation: 'Neutral'
                });
                fetchApplicationData();
            }
        } catch (error) {
            console.error('Error creating review:', error);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Completed':
                return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'In Progress':
                return <ClockIcon className="w-5 h-5 text-yellow-500" />;
            case 'Cancelled':
                return <XCircleIcon className="w-5 h-5 text-red-500" />;
            default:
                return <ClockIcon className="w-5 h-5 text-gray-500" />;
        }
    };

    const getRecommendationColor = (recommendation) => {
        switch (recommendation) {
            case 'Strongly Recommend':
                return 'text-green-600 bg-green-50';
            case 'Recommend':
                return 'text-green-500 bg-green-50';
            case 'Neutral':
                return 'text-yellow-600 bg-yellow-50';
            case 'Not Recommend':
                return 'text-red-500 bg-red-50';
            case 'Strongly Not Recommend':
                return 'text-red-600 bg-red-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col min-h-screen">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading assessment data...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!applicationData) {
        return (
            <div className="flex flex-col min-h-screen">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-gray-600">Application not found</p>
                        <button 
                            onClick={() => navigate('/recruiter-dashboard')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                <div className="w-full mb-8">
                    {/* Header */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Assessment - {applicationData.candidate_name || applicationData.candidate_email}
                                </h1>
                                <p className="text-gray-600">
                                    {applicationData.job_title} • Applied on {new Date(applicationData.application_date).toLocaleDateString()}
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/recruiter-dashboard')}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                ← Back to Dashboard
                            </button>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-8">
                            {['overview', 'assessments', 'reviews'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                                        activeTab === tab
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Candidate Info */}
                            <div className="lg:col-span-2">
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <UserIcon className="w-5 h-5 mr-2" />
                                        Candidate Information
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">Name</label>
                                            <p className="text-gray-900">{applicationData.firstName} {applicationData.lastName}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">Email</label>
                                            <p className="text-gray-900">{applicationData.candidate_email}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">Status</label>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                applicationData.status === 'Hired' ? 'bg-green-100 text-green-800' :
                                                applicationData.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {applicationData.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">Resume</label>
                                            {applicationData.resume_name ? (
                                                <a 
                                                    href={`${API_BASE_URL}/uploads/resumes/${applicationData.resume_name}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    View Resume
                                                </a>
                                            ) : (
                                                <p className="text-gray-500">No resume uploaded</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="space-y-4">
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold mb-4">Assessment Summary</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total Assessments</span>
                                            <span className="font-semibold">{assessments.length}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Completed</span>
                                            <span className="font-semibold text-green-600">
                                                {assessments.filter(a => a.status === 'Completed').length}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Reviews</span>
                                            <span className="font-semibold">{reviews.length}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Average Score</span>
                                            <span className="font-semibold">
                                                {assessments.length > 0 
                                                    ? Math.round(assessments.reduce((acc, a) => acc + (a.percentage || 0), 0) / assessments.length)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'assessments' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-semibold">Assessments</h3>
                                <button
                                    onClick={() => setShowAssessmentForm(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Create Assessment
                                </button>
                            </div>

                            <div className="grid gap-4">
                                {assessments.map((assessment) => (
                                    <div key={assessment.id} className="bg-white rounded-lg shadow p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                {getStatusIcon(assessment.status)}
                                                <h4 className="ml-2 font-semibold">{assessment.assessment_type}</h4>
                                            </div>
                                            <span className="text-sm text-gray-500">
                                                {new Date(assessment.assessment_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-3 gap-4 mb-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">Score</label>
                                                <p className="text-lg font-semibold">
                                                    {assessment.score ? `${assessment.score}/${assessment.max_score}` : 'Not scored'}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">Percentage</label>
                                                <p className="text-lg font-semibold">
                                                    {assessment.percentage ? `${Math.round(assessment.percentage)}%` : 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">Status</label>
                                                <p className="text-sm">{assessment.status}</p>
                                            </div>
                                        </div>
                                        
                                        {assessment.notes && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 mb-1">Notes</label>
                                                <p className="text-gray-700">{assessment.notes}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                
                                {assessments.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No assessments created yet. Click "Create Assessment" to get started.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-semibold">Reviews</h3>
                                <button
                                    onClick={() => { if (!hasMyReview) setShowReviewForm(true); }}
                                    disabled={hasMyReview}
                                    className={`px-4 py-2 rounded-lg text-white ${hasMyReview ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                >
                                    {hasMyReview ? 'Reviewed' : 'Add Review'}
                                </button>
                            </div>

                            <div className="grid gap-4">
                                {reviews.map((review) => (
                                    <div key={review.id} className="bg-white rounded-lg shadow p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <StarIcon className="w-5 h-5 text-yellow-500" />
                                                <h4 className="ml-2 font-semibold">{review.review_type}</h4>
                                                <div className="ml-4 flex">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <StarIcon
                                                            key={star}
                                                            className={`w-4 h-4 ${
                                                                star <= review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRecommendationColor(review.recommendation)}`}>
                                                    {review.recommendation}
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    {new Date(review.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            {review.feedback && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">Feedback</label>
                                                    <p className="text-gray-700">{review.feedback}</p>
                                                </div>
                                            )}
                                            
                                            {review.strengths && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">Strengths</label>
                                                    <p className="text-gray-700">{review.strengths}</p>
                                                </div>
                                            )}
                                            
                                            {review.areas_for_improvement && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">Areas for Improvement</label>
                                                    <p className="text-gray-700">{review.areas_for_improvement}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                {reviews.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No reviews added yet. Click "Add Review" to provide feedback.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Assessment Form Modal */}
                {showAssessmentForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h3 className="text-lg font-semibold mb-4">Create Assessment</h3>
                            <form onSubmit={handleCreateAssessment}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Assessment Type
                                        </label>
                                        <select
                                            value={assessmentForm.assessment_type}
                                            onChange={(e) => setAssessmentForm({...assessmentForm, assessment_type: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            <option value="Test">Test</option>
                                            <option value="Interview">Interview</option>
                                            <option value="Portfolio Review">Portfolio Review</option>
                                            <option value="Custom">Custom</option>
                                        </select>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Score
                                            </label>
                                            <input
                                                type="number"
                                                value={assessmentForm.score}
                                                onChange={(e) => setAssessmentForm({...assessmentForm, score: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                min="0"
                                                max={assessmentForm.max_score}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Max Score
                                            </label>
                                            <input
                                                type="number"
                                                value={assessmentForm.max_score}
                                                onChange={(e) => setAssessmentForm({...assessmentForm, max_score: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                                min="1"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={assessmentForm.status}
                                            onChange={(e) => setAssessmentForm({...assessmentForm, status: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Notes
                                        </label>
                                        <textarea
                                            value={assessmentForm.notes}
                                            onChange={(e) => setAssessmentForm({...assessmentForm, notes: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowAssessmentForm(false)}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Create Assessment
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Review Form Modal */}
                {showReviewForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                            <h3 className="text-lg font-semibold mb-4">Add Review</h3>
                            <form onSubmit={handleCreateReview}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Review Type
                                        </label>
                                        <select
                                            value={reviewForm.review_type}
                                            onChange={(e) => setReviewForm({...reviewForm, review_type: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            <option value="Technical Skills">Technical Skills</option>
                                            <option value="Communication">Communication</option>
                                            <option value="Cultural Fit">Cultural Fit</option>
                                            <option value="Overall">Overall</option>
                                            <option value="Custom">Custom</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rating (1-5)
                                        </label>
                                        <select
                                            value={reviewForm.rating}
                                            onChange={(e) => setReviewForm({...reviewForm, rating: parseInt(e.target.value)})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            {[1, 2, 3, 4, 5].map(num => (
                                                <option key={num} value={num}>{num} Star{num > 1 ? 's' : ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Recommendation
                                        </label>
                                        <select
                                            value={reviewForm.recommendation}
                                            onChange={(e) => setReviewForm({...reviewForm, recommendation: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        >
                                            <option value="Strongly Recommend">Strongly Recommend</option>
                                            <option value="Recommend">Recommend</option>
                                            <option value="Neutral">Neutral</option>
                                            <option value="Not Recommend">Not Recommend</option>
                                            <option value="Strongly Not Recommend">Strongly Not Recommend</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Feedback
                                        </label>
                                        <textarea
                                            value={reviewForm.feedback}
                                            onChange={(e) => setReviewForm({...reviewForm, feedback: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                            rows="3"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Strengths
                                        </label>
                                        <textarea
                                            value={reviewForm.strengths}
                                            onChange={(e) => setReviewForm({...reviewForm, strengths: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                            rows="2"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Areas for Improvement
                                        </label>
                                        <textarea
                                            value={reviewForm.areas_for_improvement}
                                            onChange={(e) => setReviewForm({...reviewForm, areas_for_improvement: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                            rows="2"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowReviewForm(false)}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                    >
                                        Add Review
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default AssessmentPage;
